import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ChevronDown, Plus, CheckCircle2, Calculator, Upload, GripVertical, Trash2 } from 'lucide-react';
import CustomerSelectorField from './CustomerSelectorField';
import SalespersonField from './SalespersonField';
import {
  createAccountQuote,
  updateAccountQuote,
  getAccountQuoteById,
  getAccountQuoteItems,
  createAccountQuoteItem,
  deleteAccountQuoteItem,
  uploadMenuFiles,
  deleteMenuFile,
} from '../core/_requests';
import { useAccountCustomers } from '../core/useAccountCustomers';
import { setupAxios } from '../../auth';
import SearchableSelect from '../../../shared/components/SearchableSelect'
const api = axios.create({ baseURL: process.env.REACT_APP_ACCOUNTS_API_URL || '' });
setupAxios(api);

type UploadedFile = {
  originalName: string;
  fileName: string;
  size: number;
  mimeType?: string;
  path: string;
  url?: string;
};

type LineItem = { id: number; description: string; quantity: string; rate: string };

export default function AccountsNewQuote() {
  const navigate = useNavigate();
  const { id } = useParams();
  const quoteId = id ? Number(id) : null;
  const isEditing = quoteId !== null && !Number.isNaN(quoteId);
  const formRef = useRef<HTMLFormElement | null>(null);
  const documentInputRef = useRef<HTMLInputElement | null>(null);
  const [saving, setSaving] = useState(false);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const customersHook = useAccountCustomers();
  const {
    customers,
    loading: customersLoading,
    loaded: customersLoaded,
    loadCustomers,
  } = customersHook;

  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [selectedCustomerName, setSelectedCustomerName] = useState('');
  const [salesperson, setSalesperson] = useState('');
  const [quoteNumber, setQuoteNumber] = useState(`QT-${String(Date.now()).slice(-6)}`);
  const [quoteDate, setQuoteDate] = useState(new Date().toISOString().slice(0, 10));
  const [expiryDate, setExpiryDate] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [subject, setSubject] = useState('');
  const [projectName, setProjectName] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  const [termsConditions, setTermsConditions] = useState('');
  const [quoteStatus, setQuoteStatus] = useState('Draft');
  const [statusRemarks, setStatusRemarks] = useState('');
  const [statusFilePath, setStatusFilePath] = useState('');
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [discountPercent, setDiscountPercent] = useState('0');
  const [taxType, setTaxType] = useState('TDS');
  const [taxRate, setTaxRate] = useState('');
  const [adjustment, setAdjustment] = useState('0');
  const [items, setItems] = useState<LineItem[]>([
    { id: 1, description: '', quantity: '1.00', rate: '0.00' },
  ]);

  useEffect(() => {
    if (!isEditing || quoteId == null) {
      return;
    }

    let isActive = true;
    setLoadingQuote(true);

    Promise.allSettled([getAccountQuoteById(quoteId), getAccountQuoteItems(quoteId)])
      .then(([quoteRes, itemsRes]) => {
        if (!isActive) {
          return;
        }
        if (quoteRes.status === 'fulfilled') {
          const itemRows = itemsRes.status === 'fulfilled' ? itemsRes.value : undefined;
          applyQuoteData(quoteRes.value, itemRows);
          return;
        }
        throw quoteRes.reason;
      })
      .catch((err) => {
        console.error('Failed to load quote:', err);
      })
      .finally(() => {
        if (isActive) {
          setLoadingQuote(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [quoteId, isEditing]);

  useEffect(() => {
    if (!isEditing || !selectedCustomerId) {
      return;
    }
    if (!customersLoaded && !customersLoading) {
      void loadCustomers();
    }
  }, [isEditing, selectedCustomerId, customersLoaded, customersLoading, loadCustomers]);

  const parseTaxRateFromName = (name?: string): string => {
    if (!name) return '';
    const match = name.match(/(\d+(?:\.\d+)?)\s*%/);
    return match ? match[1] : '';
  };

  const buildUploadedFiles = (raw: any): UploadedFile[] => {
    const entries = Array.isArray(raw)
      ? raw
      : typeof raw === 'string'
        ? raw.split(',')
        : [];

    return entries
      .map((entry) => (typeof entry === 'string' ? entry.replace(/\\/g, '/').trim() : ''))
      .filter(Boolean)
      .map((entry) => {
        const fileName = entry.split('/').pop() || entry;
        let url = entry;
        if (!/^https?:\/\//i.test(entry)) {
          const basePath = entry.startsWith('/') ? entry : `/${entry}`;
          const baseUrl = api.defaults.baseURL?.replace(/\/?api\/?$/, '').replace(/\/$/, '') || '';
          url = `${baseUrl}${basePath.startsWith('/uploads') ? basePath : `/uploads${basePath}`}`;
        }
        return {
          originalName: fileName,
          fileName,
          size: 0,
          mimeType: '',
          path: entry,
          url,
        } as UploadedFile;
      });
  };

  const applyQuoteData = (data: any, itemsOverride?: any[]) => {
    if (!data) return;

    const today = new Date().toISOString().slice(0, 10);
    setSelectedCustomerId(data.customerId ? String(data.customerId) : '');
    setSelectedCustomerName(data.customerName || '');
    setQuoteNumber(data.quoteNumber || `QT-${String(Date.now()).slice(-6)}`);
    setQuoteDate(data.quoteDate ? new Date(data.quoteDate).toISOString().slice(0, 10) : today);
    setExpiryDate(data.expiryDate ? new Date(data.expiryDate).toISOString().slice(0, 10) : '');
    setReferenceNumber(data.referenceNumber || '');
    setSubject(data.subject || '');
    setProjectName(data.projectName || '');
    setCustomerNotes(data.customerNotes ?? data.customer_notes ?? data.customerNote ?? '');
    setTermsConditions(data.termsConditions ?? data.terms_conditions ?? data.termsCondition ?? '');
    setSalesperson(data.salesperson || '');
    setQuoteStatus(data.status || 'Draft');
    setStatusRemarks(data.statusRemarks ?? data.status_remarks ?? '');
    setStatusFilePath(data.statusFilePath ?? data.status_file_path ?? '');
    setDiscountPercent(String(data.discountPercent ?? 0));
    setTaxType(data.taxType || 'TDS');
    const taxRateValue = data.taxRate ?? parseTaxRateFromName(data.taxName);
    setTaxRate(taxRateValue ? String(taxRateValue) : '');
    setAdjustment(String(data.adjustment ?? 0));

    const filePathValue = data.filePath ?? data.filePaths ?? data.file_path ?? data.filepath ?? '';
    const existingFiles = buildUploadedFiles(filePathValue);
    setUploadedFiles(existingFiles);

    const itemsFromData = Array.isArray(data.items)
      ? data.items
      : Array.isArray(data.lineItems)
        ? data.lineItems
        : Array.isArray(data.quoteItems)
          ? data.quoteItems
          : [];
    const rawItems = Array.isArray(itemsOverride) && itemsOverride.length > 0
      ? itemsOverride
      : itemsFromData;

    if (rawItems.length > 0) {
      const sorted = [...rawItems].sort((a, b) => (a.itemOrder ?? 0) - (b.itemOrder ?? 0));
      setItems(sorted.map((item, idx) => ({
        id: Number(item.id ?? idx + 1),
        description: String(item.description || item.itemName || ''),
        quantity: String(item.quantity ?? '1.00'),
        rate: String(item.rate ?? '0.00'),
      })));
    } else {
      setItems([{ id: 1, description: '', quantity: '1.00', rate: '0.00' }]);
    }
  };

  const tdsOptions = [
    { label: 'Dividend [10%]', rate: 10 },
    { label: 'Other Interest than securities [10%]', rate: 10 },
    { label: 'Payment of contractors for Others [2%]', rate: 2 },
    { label: 'Payment of contractors HUF/Indiv [1%]', rate: 1 },
  ];
  const tcsOptions = [
    { label: 'TCS on Goods [0.1%]', rate: 0.1 }
  ];
  const activeTaxOptions = taxType === 'TDS' ? tdsOptions : tcsOptions;

  const toNumber = (value) => {
    const num = parseFloat(value);
    return Number.isFinite(num) ? num : 0;
  };

  const getRowAmount = (row) => toNumber(row.quantity) * toNumber(row.rate);
  const subTotal = items.reduce((sum, row) => sum + getRowAmount(row), 0);
  const discountAmount = subTotal * (toNumber(discountPercent) / 100);
  const adjustmentAmount = toNumber(adjustment);
  const taxableBase = Math.max(0, subTotal - discountAmount + adjustmentAmount);
  const selectedRate = taxRate ? Number(taxRate) : 0;
  const selectedTax = activeTaxOptions.find((opt) => opt.rate === selectedRate) || null;
  const taxAmountRaw = taxableBase * ((selectedTax?.rate || 0) / 100);
  const taxAmount = taxType === 'TDS' ? -taxAmountRaw : taxAmountRaw;
  const total = taxableBase + taxAmount;

  const addRow = () => {
    setItems((prev) => {
      const nextId = prev.reduce((max, row) => Math.max(max, row.id), 0) + 1;
      return [...prev, { id: nextId, description: '', quantity: '1.00', rate: '0.00' }];
    });
  };

  const updateRow = (id, field, value) => {
    setItems((prev) => prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
  };

  const removeRow = (id) => {
    setItems((prev) => prev.filter((row) => row.id !== id));
  };



  const buildLineItemPayload = (row, order) => ({
    itemOrder: order,
    itemName: row.description.trim() || null,
    description: row.description.trim() || null,
    quantity: toNumber(row.quantity),
    rate: toNumber(row.rate),
    amount: getRowAmount(row),
  });

  const syncLineItems = async (targetId: number) => {
    let existing: any[] = [];
    try {
      existing = await getAccountQuoteItems(targetId);
    } catch (err) {
      console.warn('Failed to load existing quote items:', err);
    }
    if (existing.length > 0) {
      await Promise.all(existing.map((item: any) => deleteAccountQuoteItem(targetId, item.id)));
    }
    const cleaned = items
      .map((row) => ({
        row,
        hasContent: row.description.trim().length > 0 || toNumber(row.rate) > 0 || toNumber(row.quantity) !== 1,
      }))
      .filter((entry) => entry.hasContent)
      .map((entry, index) => buildLineItemPayload(entry.row, index + 1));

    if (cleaned.length > 0) {
      await Promise.all(cleaned.map((item) => createAccountQuoteItem(targetId, item)));
    }
  };

  const triggerDocumentSelect = () => {
    documentInputRef.current?.click();
  };

  const handleDocumentChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [] as File[]);
    if (!files.length) {
      return;
    }

    setUploadingFiles(true);
    setUploadError(null);

    try {
      const uploaded = await uploadMenuFiles('quotes', files);
      const baseUrl = api.defaults.baseURL?.replace(/\/?api\/?$/, '').replace(/\/$/, '') || '';
      const formattedUploads = (uploaded as UploadedFile[]).map((f) => {
        let fileUrl = f.url || f.path;
        if (!/^https?:\/\//i.test(fileUrl)) {
          const basePath = fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`;
          fileUrl = `${baseUrl}${basePath.startsWith('/uploads') ? basePath : `/uploads${basePath}`}`;
        }
        return { ...f, url: fileUrl };
      });
      setUploadedFiles((prev) => [...prev, ...formattedUploads]);
    } catch (err) {
      const message = 'Upload failed.';
      setUploadError(message);
    } finally {
      setUploadingFiles(false);
      event.target.value = '';
    }
  };

  const handleDeleteFile = async (file: UploadedFile) => {
    try {
      if (file.fileName) {
        await deleteMenuFile('quotes', file.fileName);
      }
      setUploadedFiles((prev) => prev.filter((f) => f.path !== file.path));
    } catch (err) {
      const message = 'Delete failed.';
      setUploadError(message);
    }
  };

  const handleSave = async (statusOverride?: string) => {
    setSaving(true);
    try {
      const customer = customers.find((c) => String(c.id) === selectedCustomerId);
      const customerName = customer?.name || customer?.company || selectedCustomerName || '';
      const customerId = selectedCustomerId ? Number(selectedCustomerId) : null;
      const quoteDateIso = quoteDate || new Date().toISOString().slice(0, 10);
      const expiryDateIso = expiryDate || null;
      const uploadedPaths = uploadedFiles.map((file) => file.path).filter(Boolean);
      const status = typeof statusOverride === 'string' ? statusOverride : quoteStatus || 'Draft';
      
      const cleanedItems = items
        .map((row) => ({
          row,
          hasContent: row.description.trim().length > 0 || toNumber(row.rate) > 0 || toNumber(row.quantity) !== 1,
        }))
        .filter((entry) => entry.hasContent)
        .map((entry, index) => buildLineItemPayload(entry.row, index + 1));

      const payload = {
        quoteDate: quoteDateIso,
        expiryDate: expiryDateIso || null,
        quoteNumber,
        referenceNumber: referenceNumber || null,
        customerId,
        customerName,
        salesperson: salesperson || null,
        projectName: projectName || null,
        subject: subject || null,
        customerNotes: customerNotes || null,
        termsConditions: termsConditions || null,
        status,
        subTotal,
        discountPercent: toNumber(discountPercent),
        discountAmount,
        taxType,
        taxName: selectedTax?.label || null,
        taxAmount: Math.abs(taxAmountRaw),
        adjustment: adjustmentAmount,
        filePath: uploadedPaths.length ? uploadedPaths.join(',') : null,
        total,
        lineItems: cleanedItems,
      };

      let targetId = quoteId || null;
      if (isEditing && quoteId) {
        await updateAccountQuote(quoteId, payload);
      } else {
        const created = await createAccountQuote(payload);
        targetId = (created as any)?.id ?? (created as any)?.data?.id ?? null;
      }

      if (targetId) {
        await syncLineItems(targetId);
      }
      navigate(-1);
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 transition-all hover:border-slate-300";
  const labelClass = "block text-[11px] font-bold text-slate-600 mb-1.5";

  return (
    <>
      <div className="flex h-screen w-full overflow-hidden bg-slate-50">
        
        <div id="sidebar-overlay" className="fixed inset-0 bg-black/50 z-40 hidden md:hidden"></div>
        <main className="flex-1 flex flex-col h-full overflow-hidden relative">
          
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6" id="main-scroll">
            <div className="max-w-5xl mx-auto pb-20">
              
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <div className="text-[11px] font-bold text-blue-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <span className="text-slate-400 hover:text-blue-600 cursor-pointer transition-colors" onClick={() => navigate('/accounts')}>ACCOUNTS</span>
                    <span className="text-slate-300">/</span>
                    <span className="text-slate-400 hover:text-blue-600 cursor-pointer transition-colors" onClick={() => navigate('/accounts/sales/quotes')}>SALES</span>
                    <span className="text-slate-300">/</span>
                    <span className="text-blue-600">QUOTES</span>
                  </div>
                  <h1 className="text-3xl font-extrabold text-slate-900 leading-tight">{isEditing ? 'Edit Quote' : 'New Quote'}</h1>
                  <p className="text-sm font-medium text-slate-500 mt-1">{isEditing ? 'Update the quote' : 'Create a new quote for your customers'}</p>
                  {loadingQuote && <p className="text-xs text-slate-400 mt-1">Loading quote details...</p>}
                </div>
              </div>

              <form ref={formRef} onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="bg-white rounded-[20px] border border-slate-200 shadow-sm p-6 md:p-8">
                <fieldset disabled={quoteStatus === 'Accepted'} className={quoteStatus === 'Accepted' ? 'opacity-80' : ''}>
                  {(quoteStatus === 'Accepted' || quoteStatus === 'Declined') && (statusRemarks || statusFilePath) && (
                    <div className="mb-8 p-5 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
                      <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                        Status Notes
                        <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider ${quoteStatus === 'Accepted' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                          {quoteStatus}
                        </span>
                      </h4>
                      {statusRemarks && (
                        <p className="text-sm text-slate-600 whitespace-pre-wrap">{statusRemarks}</p>
                      )}
                      {statusFilePath && (
                        <a 
                          href={/^https?:\/\//i.test(statusFilePath) ? statusFilePath : `${api.defaults.baseURL?.replace(/\/?api\/?$/, '').replace(/\/$/, '') || ''}${statusFilePath.startsWith('/') ? statusFilePath : `/${statusFilePath}`}`} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="inline-flex items-center gap-2 text-sm text-blue-600 font-medium hover:underline bg-white px-3 py-1.5 border border-slate-200 rounded-lg shadow-sm w-fit mt-2"
                        >
                          View Attachment
                        </a>
                      )}
                    </div>
                  )}
                  
                  {/* Primary Details Section */}
                <div className="mb-8 max-w-4xl space-y-6">
                  {/* Customer Name */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Customer Name</label>
                      <span className="text-red-500 font-bold">*</span>
                    </div>
                    <div className="flex-1">
                      <CustomerSelectorField
                        value={selectedCustomerId}
                        onChange={(selectedId, name) => { setSelectedCustomerId(selectedId); setSelectedCustomerName(name); }}
                        width="100%"
                        customersHook={customersHook}
                        className={inputClass}
                      />
                    </div>
                  </div>

                  {/* Quote # */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Quote#</label>
                      <span className="text-red-500 font-bold">*</span>
                    </div>
                    <div className="flex-1 relative">
                      <input type="text" value={quoteNumber} onChange={(e) => setQuoteNumber(e.target.value)} className={inputClass} />
                    </div>
                  </div>

                  {/* Reference # */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Reference#</label>
                    </div>
                    <div className="flex-1">
                      <input type="text" value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)} className={inputClass} />
                    </div>
                  </div>

                  {/* Quote Date */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Quote Date</label>
                      <span className="text-red-500 font-bold">*</span>
                    </div>
                    <div className="flex-1">
                      <input type="date" value={quoteDate} onChange={(e) => setQuoteDate(e.target.value)} className={inputClass} />
                    </div>
                  </div>

                  {/* Expiry Date */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Expiry Date</label>
                    </div>
                    <div className="flex-1">
                      <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} className={inputClass} />
                    </div>
                  </div>

                  {/* Salesperson */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Salesperson</label>
                    </div>
                    <div className="flex-1">
                      <SalespersonField value={salesperson} onChange={setSalesperson} className={inputClass} />
                    </div>
                  </div>

                  {/* Project Name */}
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0 pt-2.5">
                      <label className="text-[13px] font-bold text-slate-700">Project Name</label>
                    </div>
                    <div className="flex-1">
                      <input type="text" value={projectName} onChange={(e) => setProjectName(e.target.value)} placeholder="Select a project" className={inputClass} />
                      <div className="text-[11px] text-slate-400 mt-2">Select a customer to associate a project.</div>
                    </div>
                  </div>

                  {/* Subject */}
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0 pt-2.5">
                      <label className="text-[13px] font-bold text-slate-700">Subject</label>
                    </div>
                    <div className="flex-1">
                      <textarea rows={2} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Let your customer know what this Quote is for" className={`${inputClass} resize-none`}></textarea>
                    </div>
                  </div>
                </div>

                {/* Item Table */}
                <div className="mb-8 overflow-visible border border-slate-200 rounded-xl shadow-sm">
                  <div className="bg-slate-50 border-b border-slate-200 px-4 py-3 flex items-center justify-between rounded-t-xl">
                    <h3 className="text-sm font-bold text-slate-800">Item Table</h3>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse min-w-175">
                      <thead>
                        <tr className="bg-white border-b border-slate-100">
                          <th className="px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider w-[45%]">Item Details</th>
                          <th className="px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-right w-[15%]">Quantity</th>
                          <th className="px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-right w-[20%]">Rate <Calculator className="w-3 h-3 inline-block ml-1 opacity-50" /></th>
                          <th className="px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-right w-[15%]">Amount</th>
                          <th className="px-4 py-3 w-[5%]"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {items.map((row, idx) => (
                          <tr key={row.id} className="bg-white group hover:bg-slate-50/50 transition-colors">
                            <td className="px-4 py-3">
                              <div className="flex items-start gap-3">
                                <GripVertical className="w-4 h-4 text-slate-300 mt-2 cursor-grab" />
                                <textarea
                                  rows={1}
                                  value={row.description}
                                  onChange={(e) => updateRow(row.id, 'description', e.target.value)}
                                  placeholder="Type or click to select an item."
                                  className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition-all resize-none placeholder:text-slate-400"
                                ></textarea>
                              </div>
                            </td>
                            <td className="px-4 py-3 align-top">
                              <input
                                type="text"
                                value={row.quantity}
                                onChange={(e) => updateRow(row.id, 'quantity', e.target.value)}
                                className="w-full text-right px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition-all"
                              />
                            </td>
                            <td className="px-4 py-3 align-top">
                              <input
                                type="text"
                                value={row.rate}
                                onChange={(e) => updateRow(row.id, 'rate', e.target.value)}
                                className="w-full text-right px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition-all"
                              />
                            </td>
                            <td className="px-4 py-3 align-top text-right">
                              <div className="pt-2 text-sm font-bold text-slate-700">
                                {getRowAmount(row).toFixed(2)}
                              </div>
                            </td>
                            <td className="px-4 py-3 align-top text-center">
                              {items.length > 1 && (
                                <button type="button" onClick={() => removeRow(row.id)} className="p-1.5 mt-0.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="bg-slate-50 border-t border-slate-200 px-4 py-3 flex flex-wrap items-center justify-between gap-2 rounded-b-xl">
                    <button type="button" onClick={addRow} className="text-xs font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1.5 bg-blue-100/50 hover:bg-blue-100 px-3 py-2 rounded-xl transition-colors">
                      <Plus className="w-3.5 h-3.5" /> Add New Row
                    </button>
                  </div>
                </div>

                {/* Additional Details & Totals */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                  <div className="lg:col-span-7 space-y-6">
                    <div>
                      <label className={labelClass}>Customer Notes</label>
                      <textarea
                        rows={3}
                        value={customerNotes}
                        onChange={(e) => setCustomerNotes(e.target.value)}
                        placeholder="Looking forward for your business."
                        className={`${inputClass} resize-none`}
                      ></textarea>
                    </div>
                    <div>
                      <label className={labelClass}>Terms & Conditions</label>
                      <textarea
                        rows={3}
                        value={termsConditions}
                        onChange={(e) => setTermsConditions(e.target.value)}
                        placeholder="Enter the terms and conditions of your business to be displayed in your transaction"
                        className={`${inputClass} resize-none`}
                      ></textarea>
                    </div>
                    <div>
                      <label className={labelClass}>Attach File(s) to Quote</label>
                      <input
                        ref={documentInputRef}
                        type="file"
                        multiple
                        hidden
                        onChange={handleDocumentChange}
                      />
                      <button
                        type="button"
                        onClick={triggerDocumentSelect}
                        disabled={uploadingFiles}
                        className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 hover:border-slate-300 rounded-xl text-sm font-bold text-slate-700 transition-colors shadow-sm disabled:opacity-50"
                      >
                        <Upload className="w-4 h-4 text-slate-400" /> {uploadingFiles ? 'Uploading...' : 'Upload File'}
                      </button>
                      <p className="text-[11px] text-slate-400 mt-2 font-medium">You can upload a maximum of 5 files, 10MB each</p>
                      {uploadError && <p className="text-[11px] text-red-500 mt-1 font-medium">{uploadError}</p>}
                      {uploadedFiles.length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {uploadedFiles.map((file) => (
                            <li key={`${file.fileName}-${file.path || ''}`} className="flex items-center gap-2 group">
                              <a href={file.url || file.path} target="_blank" rel="noopener noreferrer" className="text-[12px] font-medium text-blue-600 hover:text-blue-700 hover:underline">
                                {file.originalName || file.fileName || file.path}
                              </a>
                              <button
                                type="button"
                                onClick={() => handleDeleteFile(file)}
                                className="text-slate-400 hover:text-red-500 hover:bg-red-50 p-1 rounded transition-colors opacity-0 group-hover:opacity-100"
                                title="Delete file"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>

                  <div className="lg:col-span-5">
                    <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm font-bold text-slate-700">Sub Total</span>
                        <span className="text-sm font-bold text-slate-900">{subTotal.toFixed(2)}</span>
                      </div>

                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm font-semibold text-slate-500 w-24">Discount</span>
                        <div className="flex-1 flex items-center justify-end gap-3">
                          <div className="flex items-center w-24 bg-white border border-slate-200 rounded-xl overflow-hidden focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-50 transition-all">
                            <input type="text" value={discountPercent} onChange={(e) => setDiscountPercent(e.target.value)} className="w-full text-right px-2 py-1.5 text-sm outline-none" />
                            <div className="px-2 py-1.5 bg-slate-50 border-l border-slate-200 text-sm font-medium text-slate-500">%</div>
                          </div>
                          <span className="text-sm font-medium text-slate-700 w-20 text-right">{discountAmount.toFixed(2)}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3 w-32">
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input
                              type="radio"
                              name="taxType"
                              checked={taxType === 'TDS'}
                              onChange={() => {
                                setTaxType('TDS');
                                setTaxRate('');
                              }}
                              className="w-3.5 h-3.5 text-blue-600 focus:ring-blue-500 border-slate-300"
                            />
                            <span className="text-[12px] font-semibold text-slate-600">TDS</span>
                          </label>
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input
                              type="radio"
                              name="taxType"
                              checked={taxType === 'TCS'}
                              onChange={() => {
                                setTaxType('TCS');
                                setTaxRate('');
                              }}
                              className="w-3.5 h-3.5 text-blue-600 focus:ring-blue-500 border-slate-300"
                            />
                            <span className="text-[12px] font-semibold text-slate-600">TCS</span>
                          </label>
                        </div>
                        <div className="flex-1 flex items-center justify-end gap-3">
                          <SearchableSelect value={taxRate} onChange={(e) => setTaxRate(e.target.value)} className="w-40 px-2.5 py-1.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 outline-none focus:border-blue-400 transition-all">
                            {activeTaxOptions.length > 0 ? (
                              <>
                                <option value="">Select a Tax</option>
                                {activeTaxOptions.map((opt) => (
                                  <option key={opt.label} value={String(opt.rate)}>{opt.label}</option>
                                ))}
                              </>
                            ) : (
                              <option value="" disabled>No results found</option>
                            )}
                          </SearchableSelect>
                          <span className="text-sm font-medium text-slate-700 w-20 text-right">{taxAmount.toFixed(2)}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between mb-6">
                        <span className="text-sm font-semibold text-slate-500 w-24">Adjustment</span>
                        <div className="flex-1 flex items-center justify-end gap-3">
                          <input type="text" value={adjustment} onChange={(e) => setAdjustment(e.target.value)} className="w-24 text-right px-2.5 py-1.5 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition-all" />
                          <span className="text-sm font-medium text-slate-700 w-20 text-right">{adjustmentAmount.toFixed(2)}</span>
                        </div>
                      </div>

                      <div className="pt-4 border-t border-slate-200 flex items-center justify-between">
                        <span className="text-lg font-black text-slate-900">Total ($)</span>
                        <span className="text-xl font-black text-blue-600">{total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
                </fieldset>

                {/* Footer Actions */}
                <div className="pt-8 mt-8 border-t border-slate-100 flex items-center gap-3">
                  {quoteStatus === 'Accepted' ? (
                    <span className="text-sm font-bold text-red-500 mr-4">This quote has been accepted and cannot be edited.</span>
                  ) : (!isEditing || quoteStatus === 'Draft') ? (
                    <>
                      <button type="button" onClick={() => handleSave('Draft')} disabled={saving} className="px-6 py-2.5 bg-white border border-slate-200 text-slate-700 hover:text-slate-900 rounded-lg text-sm font-bold shadow-sm hover:shadow transition-all hover:bg-slate-50 disabled:opacity-50">
                        {isEditing ? 'Update Draft' : 'Save as Draft'}
                      </button>
                      <button type="button" onClick={() => handleSave('Submitted')} disabled={saving} className="px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-bold shadow-sm hover:shadow hover:-translate-y-0.5 transition-all disabled:opacity-50">
                        {isEditing ? 'Save and Submit' : 'Save and Submit'}
                      </button>
                    </>
                  ) : (
                    <button type="button" onClick={() => handleSave(quoteStatus)} disabled={saving} className="px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-bold shadow-sm hover:shadow hover:-translate-y-0.5 transition-all disabled:opacity-50">
                      {saving ? 'Saving...' : 'Update Quote'}
                    </button>
                  )}
                  <button type="button" onClick={() => navigate(-1)} className="px-6 py-2.5 text-slate-500 hover:text-slate-700 text-sm font-bold transition-colors">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
            
          </div>
        </main>
      </div>
      
    </>
  );
}

